// lib/google-places.ts

type ReadyFn = () => void;


// Globals owned by this module live on `window`. Declared here so we can drop
// the `(window as any)` casts that used to litter the body. The `google` shape
// is intentionally minimal — we only check for the Maps + marker libraries.
declare global {
	interface Window {
		google?: {
			maps?: {
				marker?: unknown;
			};
		};
		apiLoadedTalaria?: () => void;
		__talariaGmapsQueue?: ReadyFn[];
	}
}


/**
 * Injects the Google Maps JS API (Places library) exactly once and ensures that
 * every `loadFunction` passed in is called after `google.maps.*` is ready.
 *
 * Safe to call from multiple components.
 */
export function loadGooglePlaces(apiKey: string, loadFunction?: ReadyFn): void {

	if (typeof window === 'undefined') return;         // SSR guard

	// If the real library is already on the page, just run the callback now.
	if(window.google?.maps && window.google?.maps?.marker) {
		loadFunction?.();
		return;
	}

	// Queue the callback for later.
	window.__talariaGmapsQueue = window.__talariaGmapsQueue || [];
	if (loadFunction) window.__talariaGmapsQueue.push(loadFunction);

	// Only the *first* caller inserts <script> and defines the global callback.
	const scriptId = 'google-places-js';
	if(document.getElementById(scriptId)) return;

	// Global that Google will call once everything (maps + places) is ready.
	window.apiLoadedTalaria = () => {
		// Flush queued callbacks
		const queue: ReadyFn[] = window.__talariaGmapsQueue || [];
		queue.forEach(fn => { try { fn(); } catch (e) { console.error(e); } });
		// Clean up
		delete window.__talariaGmapsQueue;
		delete window.apiLoadedTalaria;
	};

	// Inject the loader script
	const s = document.createElement('script');
	s.id     = scriptId;
	s.src    =
		`https://maps.googleapis.com/maps/api/js` +
		`?key=${apiKey}&libraries=places,marker&v=weekly&callback=apiLoadedTalaria`;
	s.async  = true;
	s.defer  = true;
	document.head.appendChild(s);
}
